import { useState, useEffect, Fragment, useRef } from 'react';
import { gtmEventType, contactType, inquiryType, phoneType } from '@Components/Utilities/SandhillsConstants';
import { sandhillsRequest, gtmPushEvent, repositionCookieBanner } from '@Components/Utilities/SandhillsUtility';
import { loadable } from '@Components/Utilities/SandhillsLoadable';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import TranslationText from '@Components/Utilities/TranslationText';
import MediaControl from '@Components/Shared/MediaControl';
import ready from 'document-ready';
import ComplexBreadcrumb from '@Components/Shared/ComplexBreadcrumb';
import { ThemedButton, MediaButton, ThemedLinkButton } from '@Components/MuiWrappers/Button';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import translator from '@Components/Utilities/Translator';

const MainDetailPricing = loadable(() => import('@Components/Detail/MainDetailPricing'));
const MobileModalControl = loadable(() => import('@Components/Shared/Modals/MobileModalControl'));
const ShippingInfo = loadable(() => import('@Components/Detail/ShippingInfo'));
const PayPalButton = loadable(() => import('@Components/Shared/Payments/PayPalButton'));
const ExpiredListingWarning = loadable(() => import('@Components/Detail/ExpiredListingWarning'));
const DealerInfo = loadable(() => import('@Components/Detail/DealerInfo'));
const EmailModal = loadable(() => import('@Components/Shared/Modals/EmailModal'), true);

const PartsDetailView = (props) => {

    const vip = sandhills.TradeSites.React.ComponentProps['vip'];
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);

    const getInitialSelectedInquiry = () => {
        if (props.SAMModel.ShowSAMOnLoad) {
            return inquiryType.Email;
        }

        if (props.SAMModel.ShowMAOOnLoad) {
            return inquiryType.Offer
        }

        return '';
    };

    const windowSize = useWindowSize();
    const [showEmailModal, setShowEmailModal] = useState(props.SAMModel.ShowSAMOnLoad || props.SAMModel.ShowMAOOnLoad);
    const [selectedInquiry, setSelectedInquiry] = useState(getInitialSelectedInquiry());
    const [selectedContactType, setSelectedContactType] = useState(contactType.Retail);
    const [paypalSdkLoaded, setPaypalSdkLoaded] = useState(false);
    const [activeElement, setActiveElement] = useState({});
    const imageCarouselClass = 'media-container ' + (props.MediaModel.ShowThumbnails && props.MediaModel.Media.length > 0 ? '' : 'media-container_empty');

    const ref = useRef([]);

    useEffect(function initializePartsDetailPage() {
        const cookieBannerResize = () => {
            repositionCookieBanner('detail-contact-bar', 1001);
        };

        ready(() => {
            cookieBannerResize();
            window.addEventListener('resize', cookieBannerResize);
        });

        return () => {
            window.removeEventListener('resize', cookieBannerResize);
        };
    }, []);

    const onEmailModalToggle = () => {
        if (ref.current) {
            ref.current.focus();
        }
        setShowEmailModal(showEmailModal => !showEmailModal);
    }

    const openEmailModal = (inquiry, contact) => {
        if ((contact != contactType.Wholesale) && (contact != contactType.ExclusiveWholesale)) {
            contact = contactType.Retail;
        }

        if (inquiry == inquiryType.EmailThis) {
            gtmPushEvent(gtmEventType.emailThis);
        }

        setActiveElement(document.activeElement);
        setSelectedInquiry(inquiry);
        setSelectedContactType(contact);
        setShowEmailModal(showEmailModal => !showEmailModal);
    };

    const showBuyNow = props.BuyNowModel.BuyNowURL && props.BuyNowModel.BuyNowURL !== '';

    const saveIterableAction = (actionName) => {
        sandhillsRequest({
            url: routes.SaveAction,
            method: 'post',
            data: {
                'email': '',
                'actionName': actionName,
                'manufacturers': [props.Manufacturer],
                'categoryIDs': [props.CategoryID],
                'partIDs': [props.PartID],
                'conditions': [props.Condition]
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        });
    };

    const handleBuyNowClick = () => {
        saveIterableAction('buyNow');
        gtmPushEvent(gtmEventType.buyNowClick);
    }

    const handleTelClick = (e) => {
        if (e == phoneType.Rental) gtmPushEvent(gtmEventType.callRental);
        else if (e == phoneType.Wholesale) gtmPushEvent(gtmEventType.callWholesale);
        else gtmPushEvent(gtmEventType.call);
        saveIterableAction('telClick');
    }

    return (
        <div className='detail-wrapper'>
            {props.IsExpired &&
                <div className='detail__expired-listing'>
                    <ExpiredListingWarning />
                </div>}

            <div className='detail__breadcrumbs'>
                <ComplexBreadcrumb BreadCrumbModels={props.BreadCrumbs} MobileBreadCrumbIndex={props.BreadCrumbs ? props.BreadCrumbs.length - 1 : null} />
                {!currentSite.IsMobileAppContext &&
                    <ThemedLinkButton variant='text' size='small' className='detail__search-results' onClick={() => history.go(-1)}>
                        <Icon baseClassName='fas' className='fa-angle-double-left' />
                        <span className='detail__search-results-text'><TranslationText tKey='sandhills.mobileapps.searchresults' /></span>
                    </ThemedLinkButton>
                }
            </div>

            {windowSize.isMobile &&
                <div className='detail__heading'>
                    <div className='detail__title-container'>
                        <h1 className='detail__title'>{props.Title}</h1>
                    </div>
                    {props.SubTitle &&
                        <div className='detail__subtitle bold'>{props.SubTitle}</div>
                    }
                    {props.InventoryManagementUrl &&
                        <Link variant='text' size='small' href={props.InventoryManagementUrl} underline='hover' className='detail__im-link' target='_blank'
                            aria-label={"View in Inventory Management (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                            View in Inventory Management
                            <Icon baseClassName='fas' className='fa-external-link-alt' />
                        </Link>}

                    <div className='detail__category'>{translator.translate('sandhills.parts')}</div>
                </div>}

            <div className='detail__media-buttons media-buttons'>
                {props.ShowPrintThis &&
                    <MediaButton id='parts-detail-print-this' className='print-this des-print-this' onClick={() => { gtmPushEvent(gtmEventType.printListDetail); window.print(); }}>
                        <Icon baseClassName='fas' className='fa-print detail__media-buttons-icon' />
                        <TranslationText tKey='sandhills.print' />
                    </MediaButton>}
                {props.ShowEmailThis &&
                    <MediaButton id='parts-detail-share-this' className='email-this des-email-this' onClick={() => openEmailModal(inquiryType.EmailThis)}>
                        <Icon baseClassName='fas' className='fa-share-alt detail__media-buttons-icon' />
                        <TranslationText tKey='sandhillssystem.share' />
                    </MediaButton>}
            </div>

            <main id='main-content' className='detail__main-content'>
                <div className='detail__left-col'>
                    <div className={imageCarouselClass}>
                        {props.MediaModel?.Media?.length > 0 ?
                            <MediaControl {...props.MediaModel} /> :
                            <div className='media-control-no-photo' />}
                    </div>
                </div>

                <div className='detail__right-col'>
                    <div className='detail__details'>
                        {!windowSize.isMobile &&
                            <div className='detail__heading'>
                                <div className='detail__title-container'>
                                    <h1 className='detail__title'>{props.Title}</h1>
                                </div>
                                {props.SubTitle && 
                                    <div className='detail__subtitle bold'>{props.SubTitle}</div>
                                }
                                {props.InventoryManagementUrl &&
                                    <Link variant='text' size='small' href={props.InventoryManagementUrl} underline='hover' className='detail__im-link' target='_blank'
                                        aria-label={"View in Inventory Management (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                        View in Inventory Management
                                        <Icon baseClassName='fas' className='fa-external-link-alt' />
                                    </Link>}

                                <div className='detail__category'>{translator.translate('sandhills.parts')}</div>
                            </div>}

                        <MainDetailPricing {...props.PricingInfo} IsPrivateParty={props.IsPrivateParty} />

                        <div className='detail__cta-buttons'>
                            {props.BuyNowModel?.BuyNowURL &&
                                <ThemedButton
                                    id='parts-detail-page-buynow'
                                    variant='contained'
                                    size='small'
                                    href={props.BuyNowModel.BuyNowURL}
                                    target={(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : '_self'}
                                    aria-label={props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext ? props.BuyNowModel.BuyNowButtonText + ' (' + translator.translate('sandhills.opensinanewtab') + ')' : props.BuyNowModel.BuyNowButtonText}
                                    className={'gtm-buynow des-buy-now'}
                                    ref={ref}
                                    onClick={handleBuyNowClick}
                                    sx={{ flexBasis: '48%' }}>
                                    <Icon baseClassName='fas' className='fa-shopping-cart detail__purchase-options-icon gtm-noclick' />
                                    {props.BuyNowModel.BuyNowButtonText}
                                </ThemedButton>}
                            {props.PayPalGateway
                                && props.PayPalGateway?.PayPalAmount
                                && props.PayPalGateway?.PublicKey
                                && props.PayPalGateway?.PayPalCurrencyCode
                                && props.PayPalGateway?.PayPalIntent
                                && props.PayPalGateway?.LoginID
                                && props.PayPalGateway?.ProductId > 0
                                &&
                                <div className="paypal-button-container">
                                    <PayPalButton
                                        options={{
                                            clientId: props.PayPalGateway.PublicKey,
                                            merchantId: props.PayPalGateway.LoginID,
                                            intent: props.PayPalGateway.PayPalIntent.toLowerCase(),
                                            commit: false,
                                            components: "buttons",
                                            currency: props.PayPalGateway.PayPalCurrencyCode,
                                        }}
                                        locale={"en-US"}
                                        productId={props.PayPalGateway.ProductId}
                                        quantity={1}
                                        style={{
                                            size: "responsive",
                                            layout: "horizontal",
                                            color: "gold",
                                            shape: "rect",
                                            label: "buynow",
                                            height: PayPalButton.buttonHeight
                                        }}
                                        loadSdk
                                        onButtonReady={() => setPaypalSdkLoaded(true)}
                                    />
                                </div>}
                            {props.ContactInfo?.ShowSendAMessage &&
                                <ThemedButton
                                    id='parts-detail-page-send-a-message'
                                    variant='contained'
                                    size='small'
                                    className={'dealer-contact__button'}
                                    ref={ref}
                                    onClick={() => openEmailModal(inquiryType.Email, contactType.Retail)}
                                    sx={{ flexBasis: '48%' }}>
                                    <Icon baseClassName='fas' className='fa-envelope detail__purchase-options-icon' />
                                    {props.CustomEmailSellerTextForRetail ? props.CustomEmailSellerTextForRetail : <TranslationText tKey='sandhills.messageseller' />}
                                </ThemedButton>}
                        </div>
                    </div>

                    {props.ContactInfo &&
                        <div className='detail__contact-info'>
                            <DealerInfo
                                {...props.ContactInfo}
                                openEmailModal={openEmailModal}
                                contactType={contactType.Retail}
                                manufacturer={props.Manufacturer}
                                categoryID={props.CategoryID}
                                partID={props.PartID}
                                condition={props.Condition}
                                windowSize={windowSize}
                                CustomEmailSellerTextForRetail={props.CustomEmailSellerTextForRetail}
                                ref={ref}
                            />
                        </div>}

                    {((showBuyNow && props.ShippingInfo && props.ShippingInfo?.FulfillmentOptions?.length > 0) || props.IsFreeShipping) &&
                        < ShippingInfo {...props.ShippingInfo} IsFreeShipping={props.IsFreeShipping} />}
                </div>

                <div className='detail__specs'>
                    <>
                        {props.Specs &&
                            <>
                                <h3 className='detail__specs-heading'><TranslationText tKey='sandhills.groupname.general' /></h3>
                                <div className='detail__specs-wrapper'>
                                    {Object.entries(props.Specs).map((spec) =>
                                        <Fragment key={spec[0]}>
                                            <div className='detail__specs-label'>
                                                {spec[0]}
                                            </div>
                                            <div className='detail__specs-value'>
                                                {spec[1]}
                                            </div>
                                        </Fragment>)}
                                </div>
                            </>
                        }
                    </>
                </div>

                {(currentSite.IsMobileAppContext || (windowSize.isMobile && props.UseBottomMobilePadding)) &&
                    <div className='detail__mobile-padding-bottom' />
                }
                {windowSize.isMobile &&
                    <div className='detail-contact-bar'>
                        {props.UseClassicViewFlyout &&
                            <MobileModalControl
                                ClassicViewURL={props.ClassicFlyoutModel.ClassicViewURL}
                                ClassicViewImageURL={props.ClassicFlyoutModel.ClassicViewImageURL}
                            />}
                        {vip &&
                            <ThemedButton id='parts-detail-page-mobile-vip' className='contact-bar-btn black-background value-insight-portal' href={vip.VipPortalUrl} target='_blank' onClick={() => gtmPushEvent(gtmEventType.vip)} aria-label={"VIP Portal (" + translator.translate('sandhills.opensinanewtab') + ")"}>
                                <img src={vip.VipPortalImage} alt='VIP' width='32.5' height='12' />
                                VIP Portal
                            </ThemedButton>}
                        {props.BuyNowModel?.BuyNowURL &&
                            <ThemedButton
                                id='parts-detail-page-mobile-buynow'
                                className='contact-bar-btn gtm-buynow des-buy-now'
                                href={props.BuyNowModel.BuyNowURL}
                                target={(props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext) ? '_blank' : '_self'}
                                aria-label={props.BuyNowModel.BuyNowNewTab || currentSite.IsMobileAppContext ? props.BuyNowModel.BuyNowButtonText + ' (' + translator.translate('sandhills.opensinanewtab') + ')' : props.BuyNowModel.BuyNowButtonText}
                                onClick={() => handleBuyNowClick()}>
                                <i className='material-icons'>shopping_cart</i>                             
                                {props.BuyNowModel.BuyNowButtonText}
                            </ThemedButton>}
                        {props.ContactInfo?.ShowSendAMessage &&
                            <ThemedButton
                                id='parts-detail-page-mobile-send-a-message'
                                className='contact-bar-btn des-email-seller'
                                onClick={() => openEmailModal(inquiryType.Email, contactType.Retail)}
                                ref={(element) => { ref.current[inquiryType.Email + '-retail-mobile'] = element }}>
                                <i className='dealer-contact__icon material-icons'>mail_outline</i>
                                {props.CustomEmailSellerTextForRetail ? props.CustomEmailSellerTextForRetail : <TranslationText tKey='sandhills.messageseller' />}
                            </ThemedButton>}
                        {props.ContactInfo?.ShowContactInfo && props.ContactInfo?.ShowDealerPhoneNumber &&
                            <ThemedButton
                                id='parts-detail-page-mobile-dealer-phone'
                                className='contact-bar-btn des-retail-phone'
                                onClick={() => handleTelClick(props.data-phonetype)}>
                                <a href={'tel:' + props.ContactInfo.UnformattedPhone} className='contact-bar-btn des-email-seller'>
                                    <i className='material-icons' alt='phone'>phone</i>
                                    <TranslationText tKey='sandhills.callseller' />
                                </a>
                            </ThemedButton>}
                    </div>}

                {showEmailModal &&
                    <EmailModal
                        {...props.SAMModel}
                        toggleEmailModal={onEmailModalToggle}
                        selectedInquiry={selectedInquiry}
                        selectedContactType={selectedContactType}
                        DefaultCultureCode={props.DefaultCultureCode}
                    />}
            </main >
        </div >
    );
};

export default PartsDetailView;
global.PartsDetailView = PartsDetailView;